import { useState } from 'react';
import { useAuthors, useInviteAuthor, useRevokeAuthor, useReactivateAuthor } from '@/lib/api/hooks/use-authors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus, Mail, UserCheck, UserX, RotateCcw, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';

export function AuthorManagement() {
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'active' | 'revoked'>('all');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState<string | null>(null);
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);

  const { data: authors = [], isLoading } = useAuthors(statusFilter === 'all' ? undefined : statusFilter);
  const inviteAuthor = useInviteAuthor();
  const revokeAuthor = useRevokeAuthor();
  const reactivateAuthor = useReactivateAuthor();

  const handleInviteAuthor = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      await inviteAuthor.mutateAsync({ email: inviteEmail.trim() });
      toast.success('Author invitation sent successfully');
      setInviteEmail('');
      setIsInviteDialogOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to invite author');
    }
  };

  const handleRevokeAuthor = async () => {
    if (!selectedAuthor) return;

    try {
      await revokeAuthor.mutateAsync(selectedAuthor);
      toast.success('Author access revoked successfully');
      setIsRevokeDialogOpen(false);
      setSelectedAuthor(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to revoke author access');
    }
  };

  const handleReactivateAuthor = async (authorId: string) => {
    try {
      await reactivateAuthor.mutateAsync(authorId);
      toast.success('Author access reactivated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reactivate author access');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: { variant: 'secondary' as const, label: 'Pending' },
      active: { variant: 'default' as const, label: 'Active' },
      revoked: { variant: 'destructive' as const, label: 'Revoked' },
    };

    const { variant, label } = variants[status as keyof typeof variants] || variants.pending;

    return <Badge variant={variant}>{label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return <div className="text-foreground">Loading authors...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Author Management
              </CardTitle>
              <CardDescription>
                Manage blog authors and their access permissions
              </CardDescription>
            </div>
            <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Invite Author
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite New Author</DialogTitle>
                  <DialogDescription>
                    Send an invitation to a new author to join the blog team.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="author@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsInviteDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleInviteAuthor} disabled={inviteAuthor.isPending}>
                    {inviteAuthor.isPending ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Filter */}
            <div className="flex items-center gap-4">
              <Label htmlFor="status-filter">Filter by status:</Label>
              <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Authors</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Authors Table */}
            {authors.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No authors found</p>
                <p className="text-sm">Invite your first author to get started</p>
              </div>
            ) : (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Invited</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {authors.map((author) => (
                      <motion.tr
                        key={author.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="hover:bg-muted/50"
                      >
                        <TableCell className="font-medium">{author.email}</TableCell>
                        <TableCell>{getStatusBadge(author.status)}</TableCell>
                        <TableCell>{formatDate(author.invitedAt)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {author.status === 'active' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedAuthor(author.id);
                                  setIsRevokeDialogOpen(true);
                                }}
                              >
                                <UserX className="h-4 w-4 mr-1" />
                                Revoke
                              </Button>
                            )}
                            {author.status === 'revoked' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReactivateAuthor(author.id)}
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Reactivate
                              </Button>
                            )}
                            {author.status === 'pending' && (
                              <Badge variant="outline" className="text-xs">
                                <Mail className="h-3 w-3 mr-1" />
                                Invitation Sent
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={isRevokeDialogOpen} onOpenChange={setIsRevokeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Author Access</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke this author's access? They will no longer be able to log in or manage their blog posts, but their posts will remain visible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRevokeDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevokeAuthor} disabled={revokeAuthor.isPending}>
              {revokeAuthor.isPending ? 'Revoking...' : 'Revoke Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
